import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { usePageSEO } from '../hooks/usePageSEO';
import { track } from '../utils/analytics';
import { MarketingHeader, MarketingFooter } from './OtherPages';
import {
  ArrowRight, Mail, Send, CheckCircle2, Clock, Search, Upload, X, FileText,
  Target, CreditCard, Wrench, Zap, Smile, Globe, ShieldCheck, Star, Users,
  Lock, AlertCircle, MessageCircle, ChevronRight, ChevronDown, Sparkles, Bot,
} from 'lucide-react';

/* ═══════════════════════════
   DATA
═══════════════════════════ */
const QUICK_HELP = [
  { Icon: FileText, title: 'Resume Support', desc: 'Formatting, content, templates, and export issues.', category: 'Resume Review' },
  { Icon: Target, title: 'ATS Checker', desc: 'Score results, scan errors, and keyword matching.', category: 'Technical Issue' },
  { Icon: CreditCard, title: 'Billing & Subscription', desc: 'Plans, payments, invoices, and refunds.', category: 'Billing' },
  { Icon: Wrench, title: 'Technical Support', desc: 'Login issues, bugs, and account problems.', category: 'Technical Issue' },
];

const HELP_TOPICS = [
  { label: 'ATS Score', path: '/ats-checker' },
  { label: 'Resume Templates', path: '/templates' },
  { label: 'Subscription', path: '/pricing' },
  { label: 'Interview Practice', path: '/ai-video-interview' },
  { label: 'LinkedIn Optimizer', path: '/signup' },
  { label: 'Resume Upload', path: '/build/resume' },
];

const CATEGORY_OPTIONS = ['General Question', 'Resume Review', 'Billing', 'Technical Issue', 'Feature Request', 'Partnership'];

const QUICK_LINKS = [
  { label: 'ATS Guide', Icon: Target, path: '/ats-guide' },
  { label: 'Resume Tips', Icon: FileText, path: '/resume-tips' },
  { label: 'Interview Prep', Icon: Users, path: '/ai-video-interview' },
  { label: 'Career Blog', Icon: Sparkles, path: '/blog' },
  { label: 'FAQ', Icon: MessageCircle, path: null },
];

const HERO_STATS = [
  { Icon: Zap, value: 12, prefix: 'Under ', suffix: 'h', label: 'Response Time' },
  { Icon: Smile, value: 98, suffix: '%', label: 'Satisfaction' },
  { Icon: Globe, value: 50, suffix: '+', label: 'Countries' },
];

const FAQS = [
  ['Can I upload my resume?', 'Yes — attach a PDF or DOCX (up to 5MB) to the contact form below for a manual review, or upload it directly in the builder to auto-fill your details.'],
  ['How long does support take?', 'Most messages get a reply within 12 hours during business days. Complex billing or technical issues may occasionally take a little longer.'],
  ['How do refunds work?', "If Pro isn't right for you, contact support within 7 days of upgrading and we'll issue a full refund — no questions asked."],
  ['How do AI credits work?', "There's no credit system to manage — the Free plan includes a limited number of AI generations per month, and Pro unlocks unlimited AI generation for one flat price."],
  ['Can I cancel anytime?', 'Yes. No lock-in on either plan. Cancel from your account settings and your Pro access continues until the end of the billing period — your documents are never deleted.'],
  ['How do I upgrade?', 'Open your dashboard and select Upgrade to Pro, or visit the Pricing page. Upgrades activate instantly.'],
];

const TRUST_ITEMS = [
  { Icon: Lock, kind: 'static', label: 'GDPR & SSL Secured' },
  { Icon: Star, kind: 'count', value: 4.9, decimals: 1, suffix: '/5', label: 'User Rating' },
  { Icon: Users, kind: 'count', value: 10000, decimals: 0, suffix: '+', label: 'Professionals Helped' },
  { Icon: Zap, kind: 'static', label: 'Fast Support Response' },
];

const RESUME_MIME_TYPES = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

/* ═══════════════════════════
   SHARED PIECES
═══════════════════════════ */
const SectionHeading = ({ eyebrow, title, subtitle }) => (
  <div className="ct-sec-head">
    {eyebrow && <div className="ct-sec-eyebrow">{eyebrow}</div>}
    <h2>{title}</h2>
    {subtitle && <p>{subtitle}</p>}
  </div>
);

const AnimatedCounter = ({ value, decimals = 0, duration = 1400, suffix = '', prefix = '' }) => {
  const ref = useRef(null);
  const startedRef = useRef(false);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !startedRef.current) {
        startedRef.current = true;
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setDisplay(value * eased);
          if (progress < 1) requestAnimationFrame(step);
          else setDisplay(value);
        };
        requestAnimationFrame(step);
        observer.disconnect();
      }
    }, { threshold: 0.4 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [value, duration]);

  return <span ref={ref}>{prefix}{display.toFixed(decimals)}{suffix}</span>;
};

/* ═══════════════════════════
   HERO
═══════════════════════════ */
const HeroSection = ({ onContact, onBrowse }) => (
  <section className="ct-hero">
    <div className="ct-hero-grid-bg" />
    <div className="ct-hero-glow" />
    <div className="ct-hero-inner">
      <motion.div className="ct-hero-left" initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
        <div className="ct-hero-badge"><MessageCircle size={12} /> Contact Support</div>
        <h1>Need <span className="ct-hero-highlight">help</span>?<br />We're here for you.</h1>
        <p>Questions about resumes, ATS scores, subscriptions, billing, interviews, or your account? Our support team typically replies within 12 hours.</p>

        <div className="ct-hero-stats">
          {HERO_STATS.map(s => (
            <div key={s.label} className="ct-hero-stat">
              <div className="ct-hero-stat-icon"><s.Icon size={15} /></div>
              <div>
                <div className="ct-hero-stat-value"><AnimatedCounter value={s.value} prefix={s.prefix} suffix={s.suffix} /></div>
                <div className="ct-hero-stat-label">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="ct-hero-ctas">
          <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} type="button" className="ct-btn-primary" onClick={onContact}>
            Contact Support <ArrowRight size={16} />
          </motion.button>
          <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} type="button" className="ct-btn-ghost-dark" onClick={onBrowse}>
            Browse Help Center
          </motion.button>
        </div>
      </motion.div>

      <motion.div className="ct-hero-right" initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}>
        <div className="ct-illu-glow" />
        <motion.div className="ct-illu-chat" animate={{ y: [0, -10, 0] }} transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}>
          <div className="ct-illu-chat-head">
            <div className="ct-illu-chat-avatar"><Bot size={16} /></div>
            <div>
              <div className="ct-illu-chat-name">MarsResume Support</div>
              <div className="ct-illu-chat-status"><span className="ct-illu-dot" /> Online now</div>
            </div>
          </div>
          <div className="ct-illu-bubble ct-illu-bubble-in">Hi! How can we help with your resume today?</div>
          <div className="ct-illu-bubble ct-illu-bubble-out">My ATS score dropped after switching templates.</div>
          <div className="ct-illu-bubble ct-illu-bubble-in">Got it — let's take a look together.</div>
          <div className="ct-illu-chat-input"><span>Type a message…</span><Send size={13} /></div>
        </motion.div>

        <motion.div className="ct-illu-float ct-illu-float-1" animate={{ y: [0, -12, 0] }} transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }}>
          <FileText size={15} /> Resume Preview
        </motion.div>
        <motion.div className="ct-illu-float ct-illu-float-2" animate={{ y: [0, 10, 0] }} transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut', delay: 0.8 }}>
          <Target size={15} /> ATS Score 94%
        </motion.div>
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════
   QUICK HELP CATEGORIES
═══════════════════════════ */
const QuickHelpSection = ({ onSelect }) => (
  <section className="ct-section">
    <SectionHeading eyebrow="Get Support" title="What do you need help with?" subtitle="Pick a category and we'll route your message to the right team." />
    <div className="ct-quick-grid">
      {QUICK_HELP.map((c, i) => (
        <motion.button key={c.title} type="button" className="ct-quick-card" onClick={() => onSelect(c.category)}
          initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.4, delay: i * 0.06 }} whileHover={{ y: -6 }}>
          <motion.div className="ct-quick-icon" whileHover={{ rotate: 8 }}><c.Icon size={24} /></motion.div>
          <div className="ct-quick-title">{c.title}</div>
          <p className="ct-quick-desc">{c.desc}</p>
          <span className="ct-quick-arrow"><ArrowRight size={15} /></span>
        </motion.button>
      ))}
    </div>
  </section>
);

/* ═══════════════════════════
   SEARCH HELP CENTER
═══════════════════════════ */
const SearchSection = ({ query, setQuery, onSearch }) => (
  <section className="ct-section ct-section-tint">
    <div className="ct-search-wrap">
      <SectionHeading title="Search our Help Center" />
      <form className="ct-search-bar" onSubmit={onSearch}>
        <Search size={18} />
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search our Help Center…" />
        <button type="submit" className="ct-search-btn">Search</button>
      </form>
      <div className="ct-search-chips">
        {HELP_TOPICS.map(t => (
          <button key={t.label} type="button" className="ct-chip" onClick={() => onSearch(null, t.path)}>{t.label}</button>
        ))}
      </div>
    </div>
  </section>
);

/* ═══════════════════════════
   FAQ
═══════════════════════════ */
const FAQSection = ({ faqRef }) => {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section className="ct-section" ref={faqRef}>
      <SectionHeading title="Frequently Asked Questions" />
      <div className="ct-faq-list">
        {FAQS.map(([q, a], i) => {
          const isOpen = openIdx === i;
          return (
            <div key={q} className={`ct-faq-item${isOpen ? ' open' : ''}`}>
              <button type="button" className="ct-faq-trigger" aria-expanded={isOpen} onClick={() => setOpenIdx(isOpen ? null : i)}>
                <span>{q}</span>
                <span className="ct-faq-icon">
                  <span className="bar h" />
                  <motion.span className="bar v" animate={{ rotate: isOpen ? 90 : 0 }} transition={{ duration: 0.25, ease: 'easeInOut' }} />
                </span>
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} style={{ overflow: 'hidden' }}>
                    <div className="ct-faq-answer">{a}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </section>
  );
};

/* ═══════════════════════════
   TRUST
═══════════════════════════ */
const TrustSection = () => (
  <section className="ct-section ct-section-tint">
    <SectionHeading title="Why People Trust MarsResume" />
    <div className="ct-trust-grid">
      {TRUST_ITEMS.map((t, i) => (
        <motion.div key={t.label} className="ct-trust-card" initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.4, delay: i * 0.06 }}>
          <div className="ct-trust-icon"><t.Icon size={22} /></div>
          {t.kind === 'count' && (
            <div className="ct-trust-value"><AnimatedCounter value={t.value} decimals={t.decimals} suffix={t.suffix} /></div>
          )}
          <div className="ct-trust-label">{t.label}</div>
        </motion.div>
      ))}
    </div>
  </section>
);

/* ═══════════════════════════
   FINAL CTA
═══════════════════════════ */
const FinalCTASection = ({ goTo, navigate }) => (
  <section className="ct-final-cta-outer">
    <div className="ct-final-cta-card">
      <div className="ct-final-cta-mesh" />
      <div className="ct-final-cta-glow" />
      <div className="ct-final-cta-particle p1" />
      <div className="ct-final-cta-particle p2" />
      <div className="ct-final-cta-particle p3" />
      <motion.div className="ct-final-cta-content" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.5 }}>
        <h2>Ready to build a resume that gets interviews?</h2>
        <p>Create your resume, optimize it for ATS, tailor it for every job, practice interviews, and track applications — all in one platform.</p>
        <div className="ct-final-cta-actions">
          <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} type="button" className="ct-btn-primary" style={{ padding: '14px 26px', fontSize: 15 }} onClick={() => goTo('/signup')}>
            Create Resume Free <ArrowRight size={16} />
          </motion.button>
          <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} type="button" className="ct-btn-ghost-dark" onClick={() => navigate('/templates')}>
            View Templates
          </motion.button>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════
   MAIN PAGE
═══════════════════════════ */
export const ContactPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const faqRef = useRef(null);

  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [category, setCategory] = useState(CATEGORY_OPTIONS[0]);
  const [status, setStatus] = useState('idle');
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [query, setQuery] = useState('');

  usePageSEO({
    title: 'Contact Support – MarsResume | We Reply Within 12 Hours',
    description: 'Questions about resumes, ATS scores, subscriptions, billing, or interviews? Contact MarsResume support — our team typically replies within 12 hours.',
    canonical: '/contact',
  });

  const apiBase = process.env.REACT_APP_API_URL || '/api';
  const goTo = (path) => navigate(user ? (path === '/signup' ? '/dashboard' : path) : path);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const scrollToFAQ = () => faqRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const handleQuickSelect = (cat) => { setCategory(cat); scrollToForm(); };

  const handleSearch = (e, directPath) => {
    if (e) e.preventDefault();
    if (directPath) { goTo(directPath); return; }
    const q = query.trim().toLowerCase();
    if (!q) return;
    const hit = HELP_TOPICS.find(t => t.label.toLowerCase().includes(q) || q.includes(t.label.toLowerCase()));
    if (hit) goTo(hit.path);
    else scrollToFAQ();
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!RESUME_MIME_TYPES.includes(f.type)) { setFileError('Only PDF or DOCX files are supported.'); return; }
    if (f.size > 5 * 1024 * 1024) { setFileError('File must be under 5MB.'); return; }
    setFileError('');
    setFile(f);
  };

  const resetForm = () => {
    setForm({ name: '', email: '', subject: '', message: '' });
    setCategory(CATEGORY_OPTIONS[0]);
    setFile(null);
    setFileError('');
    setStatus('idle');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus('sending');
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('email', form.email);
      fd.append('subject', form.subject);
      fd.append('message', form.message);
      fd.append('category', category);
      if (file) fd.append('resume', file);
      const res = await fetch(`${apiBase}/contact`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error();
      setStatus('sent');
      track('contact_form_submitted');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="ct-page">
      <ContactStyles />
      <MarketingHeader />

      <HeroSection onContact={scrollToForm} onBrowse={() => navigate('/ats-guide')} />
      <QuickHelpSection onSelect={handleQuickSelect} />
      <SearchSection query={query} setQuery={setQuery} onSearch={handleSearch} />

      <section className="ct-section" ref={formRef}>
        <div className="ct-main-grid">
          {/* ── Contact form ── */}
          <div className="ct-form-card">
            <div className="ct-form-head">
              <div className="ct-form-head-icon"><Send size={18} /></div>
              <div>
                <div className="ct-form-title">Send us a Message</div>
                <div className="ct-form-sub">We typically reply within 12 hours</div>
              </div>
            </div>

            {status === 'sent' ? (
              <div className="ct-success">
                <div className="ct-success-icon"><CheckCircle2 size={30} /></div>
                <div className="ct-success-title">Message sent!</div>
                <p>Thanks for reaching out — we'll get back to you at <strong>{form.email}</strong> within 12 hours.</p>
                <button type="button" className="ct-btn-secondary" onClick={resetForm}>Send another message</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="ct-form-row">
                  <div className="ct-field">
                    <label>Full Name *</label>
                    <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="John Smith" required />
                  </div>
                  <div className="ct-field">
                    <label>Email Address *</label>
                    <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="you@example.com" required />
                  </div>
                </div>

                <div className="ct-field">
                  <label>Category</label>
                  <div className="ct-select-wrap">
                    <select value={category} onChange={e => setCategory(e.target.value)}>
                      {CATEGORY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <ChevronDown size={15} className="ct-select-chevron" />
                  </div>
                </div>

                <div className="ct-field">
                  <label>Subject</label>
                  <input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} placeholder="What's this about?" />
                </div>

                <div className="ct-field">
                  <label>Message *</label>
                  <textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} placeholder="Tell us how we can help…" required rows={6} maxLength={2000} />
                  <div className={`ct-char-count${form.message.length > 1800 ? ' near' : ''}`}>{form.message.length} / 2000</div>
                </div>

                <div className="ct-field">
                  <label>Resume Upload <span className="ct-field-optional">(optional)</span></label>
                  {file ? (
                    <div className="ct-file-chip">
                      <FileText size={14} /> {file.name}
                      <button type="button" onClick={() => setFile(null)} aria-label="Remove file"><X size={13} /></button>
                    </div>
                  ) : (
                    <label className="ct-dropzone">
                      <Upload size={18} />
                      <span>Click to upload or drag and drop</span>
                      <span className="ct-dropzone-sub">PDF or DOCX, up to 5MB</span>
                      <input type="file" accept=".pdf,.docx" hidden onChange={handleFile} />
                    </label>
                  )}
                  {fileError && <div className="ct-field-error"><AlertCircle size={13} /> {fileError}</div>}
                </div>

                {status === 'error' && (
                  <div className="ct-form-error"><AlertCircle size={15} /> Something went wrong. Email us at <strong>support@marsresume.com</strong></div>
                )}

                <motion.button whileHover={{ scale: status === 'sending' ? 1 : 1.02 }} whileTap={{ scale: status === 'sending' ? 1 : 0.98 }}
                  type="submit" className="ct-btn-submit" disabled={status === 'sending'}>
                  {status === 'sending' ? (
                    <><span className="ct-spinner" /> Sending…</>
                  ) : (
                    <>Send Message <ArrowRight size={16} /></>
                  )}
                </motion.button>
                <div className="ct-form-reply-time"><Clock size={13} /> Average reply time: <strong>Within 12 hours</strong></div>
              </form>
            )}
          </div>

          {/* ── Sticky sidebar ── */}
          <aside className="ct-sidebar">
            <div className="ct-side-card">
              <div className="ct-side-row">
                <span className="ct-side-label">Support Status</span>
                <span className="ct-side-status"><span className="ct-status-dot" /> Online</span>
              </div>
              <div className="ct-side-divider" />
              <div className="ct-side-item">
                <Clock size={15} />
                <div>
                  <div className="ct-side-item-label">Business Hours</div>
                  <div className="ct-side-item-value">Mon–Fri, 9AM–6PM PKT</div>
                </div>
              </div>
              <div className="ct-side-item">
                <Mail size={15} />
                <div>
                  <div className="ct-side-item-label">Email</div>
                  <a href="mailto:support@marsresume.com" className="ct-side-item-value ct-side-link">support@marsresume.com</a>
                </div>
              </div>
              <div className="ct-side-item">
                <Zap size={15} />
                <div>
                  <div className="ct-side-item-label">Response Time</div>
                  <div className="ct-side-item-value">Under 12 hours</div>
                </div>
              </div>
            </div>

            <div className="ct-side-card">
              <div className="ct-side-label" style={{ marginBottom: 14 }}>Quick Links</div>
              {QUICK_LINKS.map(l => (
                <button key={l.label} type="button" className="ct-side-link-row"
                  onClick={() => l.path ? goTo(l.path) : scrollToFAQ()}>
                  <l.Icon size={15} /> {l.label}
                  <ChevronRight size={14} className="ct-side-link-arrow" />
                </button>
              ))}
            </div>

            <div className="ct-side-card ct-side-trust">
              <div className="ct-side-label" style={{ marginBottom: 12 }}>Why trust us</div>
              <div className="ct-side-trust-row"><ShieldCheck size={14} /> SSL encrypted & GDPR compliant</div>
              <div className="ct-side-trust-row"><Star size={14} /> 4.9/5 average rating</div>
            </div>
          </aside>
        </div>
      </section>

      <FAQSection faqRef={faqRef} />
      <TrustSection />
      <FinalCTASection goTo={goTo} navigate={navigate} />

      <MarketingFooter navigate={navigate} goTo={goTo} />
    </div>
  );
};

/* ═══════════════════════════
   STYLES
═══════════════════════════ */
const ContactStyles = () => (
  <style>{`
    .ct-page { min-height: 100vh; background: #FFFFFF; font-family: 'Inter', sans-serif; color: #111827; overflow-x: clip; }

    .ct-btn-primary { background:#EF1D28;color:white;border:none;border-radius:12px;padding:13px 24px;font-weight:700;font-size:14px;cursor:pointer;font-family:inherit;display:inline-flex;align-items:center;gap:8px;transition:box-shadow 0.2s, background 0.2s; box-shadow:0 4px 16px rgba(239,29,40,0.25); }
    .ct-btn-primary:hover { background:#D91620; box-shadow:0 10px 26px rgba(239,29,40,0.35); }
    .ct-btn-ghost-dark { background: rgba(255,255,255,0.06); border: 1.5px solid rgba(255,255,255,0.2); color: white; border-radius: 12px; padding: 13px 24px; font-weight: 700; font-size: 14px; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 8px; transition: background 0.2s, border-color 0.2s; }
    .ct-btn-ghost-dark:hover { background: rgba(255,255,255,0.12); border-color: rgba(255,255,255,0.35); }
    .ct-btn-secondary { display: inline-flex; align-items: center; gap: 8px; background: white; border: 1.5px solid #E5E7EB; color: #374151; border-radius: 11px; padding: 11px 22px; font-weight: 700; font-size: 13.5px; cursor: pointer; font-family: inherit; transition: all 0.2s; }
    .ct-btn-secondary:hover { border-color: #D1D5DB; background: #F9FAFB; }

    /* Section shell */
    .ct-section { padding: 88px 48px; max-width: 1180px; margin: 0 auto; }
    .ct-section-tint { background: #FAFBFF; max-width: none; padding-left: 48px; padding-right: 48px; }
    .ct-section-tint > * { max-width: 1180px; margin-left: auto; margin-right: auto; }
    .ct-sec-head { text-align: center; margin-bottom: 48px; }
    .ct-sec-eyebrow { display: inline-flex; align-items: center; gap: 6px; background: #FFF1F2; border: 1px solid #FECACA; color: #EF1D28; border-radius: 20px; padding: 5px 16px; font-size: 11px; font-weight: 700; letter-spacing: 0.8px; text-transform: uppercase; margin-bottom: 16px; }
    .ct-sec-head h2 { margin: 0; font-size: 34px; font-weight: 900; color: #0F172A; letter-spacing: -0.8px; }
    .ct-sec-head p { margin: 12px auto 0; color: #64748B; font-size: 15px; line-height: 1.65; max-width: 560px; }

    /* Hero */
    .ct-hero { background: linear-gradient(160deg,#070E1A 0%,#0B1020 50%,#0A1420 100%); padding: 96px 48px 104px; position: relative; overflow: hidden; }
    .ct-hero-grid-bg { position: absolute; inset: 0; background-image: radial-gradient(rgba(255,255,255,0.06) 1px,transparent 1px); background-size: 28px 28px; pointer-events: none; }
    .ct-hero-glow { position: absolute; top: 5%; left: 10%; width: 720px; height: 440px; background: radial-gradient(ellipse, rgba(239,29,40,0.16) 0%, transparent 70%); pointer-events: none; }
    .ct-hero-inner { max-width: 1180px; margin: 0 auto; display: flex; align-items: center; gap: 60px; position: relative; }
    .ct-hero-left { flex: 0 0 50%; }
    .ct-hero-badge { display: inline-flex; align-items: center; gap: 7px; background: rgba(239,29,40,0.14); border: 1px solid rgba(239,29,40,0.25); border-radius: 24px; padding: 6px 16px; font-size: 11px; font-weight: 700; color: #FCA5A5; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 22px; }
    .ct-hero-left h1 { margin: 0 0 20px; font-size: 48px; line-height: 1.12; font-weight: 900; letter-spacing: -1.4px; color: white; }
    .ct-hero-highlight { background: linear-gradient(135deg,#EF1D28,#FF6B6B); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; }
    .ct-hero-left p { margin: 0 0 32px; font-size: 16.5px; line-height: 1.7; color: #94A3B8; max-width: 480px; }
    .ct-hero-stats { display: flex; gap: 26px; flex-wrap: wrap; margin-bottom: 34px; }
    .ct-hero-stat { display: flex; align-items: center; gap: 10px; }
    .ct-hero-stat-icon { width: 32px; height: 32px; border-radius: 9px; background: rgba(239,29,40,0.14); color: #FCA5A5; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .ct-hero-stat-value { font-size: 17px; font-weight: 900; color: white; letter-spacing: -0.3px; line-height: 1.2; }
    .ct-hero-stat-label { font-size: 11.5px; color: #64748B; font-weight: 500; }
    .ct-hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; }
    .ct-hero-right { flex: 1; display: flex; align-items: center; justify-content: center; position: relative; min-height: 380px; }

    /* Hero illustration */
    .ct-illu-glow { position: absolute; inset: 0; background: radial-gradient(circle at 50% 50%, rgba(239,29,40,0.18) 0%, transparent 62%); pointer-events: none; }
    .ct-illu-chat { position: relative; width: 300px; background: rgba(255,255,255,0.98); backdrop-filter: blur(18px); border-radius: 20px; box-shadow: 0 40px 90px rgba(0,0,0,0.45); border: 1px solid rgba(255,255,255,0.1); padding: 16px; }
    .ct-illu-chat-head { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
    .ct-illu-chat-avatar { width: 34px; height: 34px; border-radius: 10px; background: linear-gradient(135deg,#EF1D28,#FF6B6B); color: white; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .ct-illu-chat-name { font-size: 13px; font-weight: 800; color: #111827; }
    .ct-illu-chat-status { display: flex; align-items: center; gap: 5px; font-size: 11px; color: #059669; font-weight: 600; }
    .ct-illu-dot { width: 6px; height: 6px; border-radius: 50%; background: #22C55E; }
    .ct-illu-bubble { font-size: 12.5px; line-height: 1.5; padding: 9px 12px; border-radius: 12px; margin-bottom: 8px; max-width: 84%; }
    .ct-illu-bubble-in { background: #F3F4F6; color: #111827; border-bottom-left-radius: 4px; }
    .ct-illu-bubble-out { background: linear-gradient(135deg,#EF1D28,#D91620); color: white; margin-left: auto; border-bottom-right-radius: 4px; }
    .ct-illu-chat-input { display: flex; align-items: center; justify-content: space-between; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 10px; padding: 9px 12px; font-size: 12px; color: #9CA3AF; margin-top: 4px; }
    .ct-illu-float { position: absolute; display: flex; align-items: center; gap: 8px; background: rgba(255,255,255,0.98); border-radius: 14px; padding: 10px 14px; font-size: 12.5px; font-weight: 800; color: #111827; box-shadow: 0 16px 40px rgba(0,0,0,0.3); }
    .ct-illu-float-1 { top: -14px; right: -18px; color: #EF1D28; }
    .ct-illu-float-2 { bottom: 4px; left: -30px; color: #059669; }

    /* Quick help */
    .ct-quick-grid { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 22px; }
    .ct-quick-card { position: relative; text-align: left; background: white; border: 1.5px solid #E5E7EB; border-radius: 24px; padding: 32px 24px; cursor: pointer; font-family: inherit; overflow: hidden; transition: box-shadow 0.25s ease, border-color 0.25s ease; }
    .ct-quick-card::before { content: ''; position: absolute; inset: 0; border-radius: 24px; padding: 1.5px; background: linear-gradient(135deg,#EF1D28,#FF6B6B); opacity: 0; -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0); -webkit-mask-composite: xor; mask-composite: exclude; transition: opacity 0.25s ease; pointer-events: none; }
    .ct-quick-card:hover { box-shadow: 0 22px 50px rgba(15,23,42,0.1); }
    .ct-quick-card:hover::before { opacity: 1; }
    .ct-quick-icon { width: 54px; height: 54px; border-radius: 15px; background: linear-gradient(135deg,#FFF1F2,#FFE4E6); color: #EF1D28; display: flex; align-items: center; justify-content: center; margin-bottom: 20px; }
    .ct-quick-title { font-size: 17px; font-weight: 800; color: #111827; margin-bottom: 8px; letter-spacing: -0.2px; }
    .ct-quick-desc { margin: 0 0 18px; font-size: 13.5px; color: #6B7280; line-height: 1.6; }
    .ct-quick-arrow { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: 50%; background: #F9FAFB; color: #9CA3AF; transition: background 0.2s, color 0.2s; }
    .ct-quick-card:hover .ct-quick-arrow { background: #FFF1F2; color: #EF1D28; }

    /* Search */
    .ct-search-wrap { max-width: 680px; margin: 0 auto; text-align: center; }
    .ct-search-bar { display: flex; align-items: center; gap: 10px; background: white; border: 1.5px solid #E5E7EB; border-radius: 999px; padding: 8px 8px 8px 22px; box-shadow: 0 10px 30px rgba(15,23,42,0.06); }
    .ct-search-bar svg { color: #9CA3AF; flex-shrink: 0; }
    .ct-search-bar input { flex: 1; border: none; outline: none; font-size: 15px; font-family: inherit; color: #111827; background: transparent; padding: 8px 0; }
    .ct-search-bar input::placeholder { color: #9CA3AF; }
    .ct-search-btn { background: #EF1D28; color: white; border: none; border-radius: 999px; padding: 11px 24px; font-weight: 700; font-size: 13.5px; cursor: pointer; font-family: inherit; transition: background 0.2s; flex-shrink: 0; }
    .ct-search-btn:hover { background: #D91620; }
    .ct-search-chips { display: flex; flex-wrap: wrap; gap: 10px; justify-content: center; margin-top: 22px; }
    .ct-chip { background: white; border: 1.5px solid #E5E7EB; color: #374151; border-radius: 999px; padding: 8px 18px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit; transition: all 0.2s; }
    .ct-chip:hover { border-color: #FECACA; background: #FFF1F2; color: #EF1D28; }

    /* Form + sidebar grid */
    .ct-main-grid { display: grid; grid-template-columns: 1fr 340px; gap: 32px; align-items: start; }

    .ct-form-card { background: rgba(255,255,255,0.75); backdrop-filter: blur(20px); border: 1px solid rgba(229,231,235,0.9); border-radius: 24px; padding: 40px; box-shadow: 0 30px 80px rgba(15,23,42,0.08); }
    .ct-form-head { display: flex; align-items: center; gap: 14px; margin-bottom: 32px; }
    .ct-form-head-icon { width: 44px; height: 44px; border-radius: 13px; background: linear-gradient(135deg,#EF1D28,#D91620); color: white; display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 8px 22px rgba(239,29,40,0.3); }
    .ct-form-title { font-size: 19px; font-weight: 900; color: #0F172A; letter-spacing: -0.3px; }
    .ct-form-sub { font-size: 13px; color: #6B7280; margin-top: 2px; }

    .ct-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    .ct-field { margin-bottom: 18px; position: relative; }
    .ct-field > label:not(.ct-dropzone) { display: block; font-size: 12px; font-weight: 700; color: #374151; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px; }
    .ct-field-optional { text-transform: none; font-weight: 500; color: #9CA3AF; letter-spacing: 0; }
    .ct-field input, .ct-field select, .ct-field textarea {
      width: 100%; border: 1.5px solid #E5E7EB; border-radius: 12px; padding: 12px 15px; font-size: 14px;
      font-family: inherit; color: #111827; background: white; outline: none; box-sizing: border-box;
      transition: border-color 0.15s, box-shadow 0.15s;
    }
    .ct-field input:focus, .ct-field select:focus, .ct-field textarea:focus { border-color: #EF1D28; box-shadow: 0 0 0 3px rgba(239,29,40,0.08); }
    .ct-field textarea { resize: vertical; min-height: 130px; }
    .ct-select-wrap { position: relative; }
    .ct-select-wrap select { appearance: none; -webkit-appearance: none; padding-right: 38px; cursor: pointer; }
    .ct-select-chevron { position: absolute; right: 14px; top: 50%; transform: translateY(-50%); color: #9CA3AF; pointer-events: none; }
    .ct-char-count { text-align: right; font-size: 11.5px; color: #9CA3AF; margin-top: 6px; }
    .ct-char-count.near { color: #EF1D28; font-weight: 700; }

    .ct-dropzone { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; border: 1.5px dashed #D1D5DB; border-radius: 14px; padding: 26px 16px; text-align: center; cursor: pointer; color: #6B7280; transition: border-color 0.2s, background 0.2s; }
    .ct-dropzone:hover { border-color: #EF1D28; background: #FFF9F9; color: #EF1D28; }
    .ct-dropzone span { font-size: 13px; font-weight: 600; }
    .ct-dropzone-sub { font-size: 11.5px !important; font-weight: 500 !important; color: #9CA3AF !important; }
    .ct-file-chip { display: inline-flex; align-items: center; gap: 8px; background: #FFF1F2; border: 1px solid #FECDD3; color: #B91C1C; border-radius: 10px; padding: 10px 14px; font-size: 13px; font-weight: 600; }
    .ct-file-chip button { border: none; background: none; color: #B91C1C; cursor: pointer; display: flex; align-items: center; margin-left: 4px; padding: 0; }
    .ct-field-error { display: flex; align-items: center; gap: 6px; color: #DC2626; font-size: 12px; margin-top: 8px; font-weight: 600; }

    .ct-form-error { display: flex; align-items: center; gap: 8px; background: #FFF1F2; border: 1px solid #FECACA; border-radius: 10px; padding: 12px 16px; font-size: 13px; color: #DC2626; margin-bottom: 16px; }
    .ct-btn-submit { width: 100%; background: linear-gradient(135deg,#EF1D28,#C5161D); color: white; border: none; border-radius: 13px; padding: 15px; font-size: 15px; font-weight: 800; cursor: pointer; font-family: inherit; display: flex; align-items: center; justify-content: center; gap: 8px; box-shadow: 0 10px 28px rgba(239,29,40,0.3); }
    .ct-btn-submit:disabled { background: #9CA3AF; box-shadow: none; cursor: not-allowed; }
    .ct-spinner { width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.35); border-top-color: white; border-radius: 50%; animation: ctSpin 0.8s linear infinite; }
    @keyframes ctSpin { to { transform: rotate(360deg); } }
    .ct-form-reply-time { display: flex; align-items: center; gap: 6px; justify-content: center; font-size: 12.5px; color: #6B7280; margin-top: 16px; }

    .ct-success { text-align: center; padding: 24px 8px; }
    .ct-success-icon { width: 68px; height: 68px; border-radius: 50%; background: linear-gradient(135deg,#D1FAE5,#A7F3D0); display: flex; align-items: center; justify-content: center; color: #059669; margin: 0 auto 20px; }
    .ct-success-title { font-size: 21px; font-weight: 900; color: #111827; margin-bottom: 10px; }
    .ct-success p { font-size: 14px; color: #6B7280; line-height: 1.7; max-width: 320px; margin: 0 auto 24px; }

    /* Sidebar */
    .ct-sidebar { position: sticky; top: 96px; display: flex; flex-direction: column; gap: 16px; }
    .ct-side-card { background: white; border: 1px solid #E5E7EB; border-radius: 18px; padding: 22px; box-shadow: 0 4px 18px rgba(15,23,42,0.04); }
    .ct-side-row { display: flex; align-items: center; justify-content: space-between; }
    .ct-side-label { font-size: 12px; font-weight: 800; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.6px; }
    .ct-side-status { display: flex; align-items: center; gap: 6px; font-size: 12.5px; font-weight: 700; color: #059669; }
    .ct-status-dot { width: 7px; height: 7px; border-radius: 50%; background: #22C55E; box-shadow: 0 0 0 0 rgba(34,197,94,0.6); animation: ctPulse 1.8s infinite; }
    @keyframes ctPulse { 0% { box-shadow: 0 0 0 0 rgba(34,197,94,0.5); } 70% { box-shadow: 0 0 0 6px rgba(34,197,94,0); } 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); } }
    .ct-side-divider { height: 1px; background: #F1F5F9; margin: 16px 0; }
    .ct-side-item { display: flex; align-items: flex-start; gap: 12px; color: #6B7280; margin-bottom: 16px; }
    .ct-side-item:last-child { margin-bottom: 0; }
    .ct-side-item-label { font-size: 11px; color: #9CA3AF; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 2px; }
    .ct-side-item-value { font-size: 13.5px; color: #111827; font-weight: 700; }
    .ct-side-link { text-decoration: none; }
    .ct-side-link:hover { color: #EF1D28; }
    .ct-side-link-row { width: 100%; display: flex; align-items: center; gap: 10px; background: none; border: none; border-radius: 10px; padding: 10px 8px; font-size: 13.5px; font-weight: 700; color: #374151; cursor: pointer; font-family: inherit; text-align: left; transition: background 0.15s, color 0.15s; }
    .ct-side-link-row:hover { background: #FFF1F2; color: #EF1D28; }
    .ct-side-link-arrow { margin-left: auto; color: #D1D5DB; transition: transform 0.15s; }
    .ct-side-link-row:hover .ct-side-link-arrow { transform: translateX(2px); color: #EF1D28; }
    .ct-side-trust-row { display: flex; align-items: center; gap: 9px; font-size: 12.5px; color: #374151; font-weight: 600; margin-bottom: 10px; }
    .ct-side-trust-row:last-child { margin-bottom: 0; }
    .ct-side-trust-row svg { color: #2563EB; flex-shrink: 0; }

    /* FAQ */
    .ct-faq-list { display: flex; flex-direction: column; gap: 12px; max-width: 780px; margin: 0 auto; }
    .ct-faq-item { background: white; border: 1.5px solid #E5E7EB; border-radius: 16px; overflow: hidden; transition: border-color 0.25s ease, box-shadow 0.25s ease; }
    .ct-faq-item.open { border-color: #FECACA; box-shadow: 0 10px 30px rgba(239,29,40,0.08); }
    .ct-faq-trigger { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 20px 24px; background: transparent; border: none; cursor: pointer; text-align: left; font-family: inherit; }
    .ct-faq-trigger span:first-child { font-size: 15.5px; font-weight: 700; color: #111827; line-height: 1.4; }
    .ct-faq-icon { position: relative; width: 30px; height: 30px; border-radius: 9px; background: #F9FAFB; border: 1px solid #E5E7EB; flex-shrink: 0; transition: background 0.25s ease, border-color 0.25s ease; }
    .ct-faq-item.open .ct-faq-icon { background: #FFF1F2; border-color: #FECDD3; }
    .ct-faq-icon .bar { position: absolute; top: 50%; left: 50%; background: #6B7280; border-radius: 2px; }
    .ct-faq-icon .bar.h { width: 12px; height: 2px; margin: -1px 0 0 -6px; }
    .ct-faq-icon .bar.v { width: 2px; height: 12px; margin: -6px 0 0 -1px; }
    .ct-faq-item.open .ct-faq-icon .bar { background: #EF1D28; }
    .ct-faq-answer { padding: 0 24px 22px; font-size: 14.5px; color: #6B7280; line-height: 1.8; }

    /* Trust */
    .ct-trust-grid { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 20px; max-width: 1100px; margin: 0 auto; }
    .ct-trust-card { background: white; border: 1px solid #E5E7EB; border-radius: 20px; padding: 30px 20px; text-align: center; transition: box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease; }
    .ct-trust-card:hover { box-shadow: 0 18px 44px rgba(15,23,42,0.08); transform: translateY(-4px); border-color: #D1D5DB; }
    .ct-trust-icon { width: 52px; height: 52px; border-radius: 15px; background: linear-gradient(135deg,#FFF1F2,#FFE4E6); color: #EF1D28; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
    .ct-trust-value { font-size: 26px; font-weight: 900; color: #0F172A; letter-spacing: -0.5px; margin-bottom: 6px; }
    .ct-trust-label { font-size: 13.5px; font-weight: 700; color: #374151; }

    /* Final CTA */
    .ct-final-cta-outer { padding: 20px 48px 96px; }
    .ct-final-cta-card { max-width: 1100px; margin: 0 auto; background: linear-gradient(150deg, #060D1A 0%, #0B1020 50%, #0A1428 100%); border-radius: 28px; overflow: hidden; position: relative; padding: 72px 56px; border: 1px solid #1A2A3E; text-align: center; }
    .ct-final-cta-mesh { position: absolute; inset: 0; background-image: radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px); background-size: 28px 28px; pointer-events: none; }
    .ct-final-cta-glow { position: absolute; top: -100px; left: 50%; transform: translateX(-50%); width: 600px; height: 400px; background: radial-gradient(ellipse, rgba(239,29,40,0.18) 0%, transparent 65%); pointer-events: none; }
    .ct-final-cta-particle { position: absolute; border-radius: 50%; background: rgba(239,29,40,0.4); pointer-events: none; animation: ctParticleFloat 6s ease-in-out infinite; }
    .ct-final-cta-particle.p1 { width: 8px; height: 8px; top: 18%; left: 12%; animation-delay: 0s; }
    .ct-final-cta-particle.p2 { width: 5px; height: 5px; top: 65%; right: 14%; background: rgba(96,165,250,0.4); animation-delay: 1.4s; }
    .ct-final-cta-particle.p3 { width: 6px; height: 6px; bottom: 20%; left: 22%; animation-delay: 2.6s; }
    @keyframes ctParticleFloat { 0%,100% { transform: translateY(0); opacity: 0.5; } 50% { transform: translateY(-18px); opacity: 1; } }
    .ct-final-cta-content { position: relative; max-width: 560px; margin: 0 auto; }
    .ct-final-cta-content h2 { margin: 0 0 14px; font-size: 34px; font-weight: 900; color: white; letter-spacing: -0.8px; line-height: 1.15; }
    .ct-final-cta-content p { margin: 0 0 30px; color: #94A3B8; font-size: 15px; line-height: 1.6; }
    .ct-final-cta-actions { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }

    /* Responsive */
    @media (max-width: 1100px) {
      .ct-quick-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
      .ct-trust-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
    }
    @media (max-width: 980px) {
      .ct-main-grid { grid-template-columns: 1fr; }
      .ct-sidebar { position: static; }
    }
    @media (max-width: 900px) {
      .ct-hero { padding: 64px 24px 80px; }
      .ct-hero-inner { flex-direction: column; gap: 44px; text-align: center; }
      .ct-hero-left { flex: none; }
      .ct-hero-left p { margin-left: auto; margin-right: auto; }
      .ct-hero-stats { justify-content: center; }
      .ct-hero-ctas { justify-content: center; }
      .ct-hero-left h1 { font-size: 36px; }
      .ct-hero-right { min-height: 320px; margin-top: 12px; }
      .ct-section { padding: 56px 24px; }
      .ct-section-tint { padding-left: 24px; padding-right: 24px; }
      .ct-form-card { padding: 28px 22px; }
      .ct-final-cta-outer { padding: 12px 24px 72px; }
      .ct-final-cta-card { padding: 48px 28px; }
    }
    @media (max-width: 640px) {
      .ct-quick-grid { grid-template-columns: 1fr; }
      .ct-trust-grid { grid-template-columns: 1fr 1fr; }
      .ct-form-row { grid-template-columns: 1fr; }
      .ct-hero-left h1 { font-size: 30px; letter-spacing: -1px; }
      .ct-final-cta-content h2 { font-size: 26px; }
      .ct-illu-float { display: none; }
      .ct-illu-chat { width: 100%; max-width: 300px; }
    }
  `}</style>
);
